import React, { useState } from 'react';
import { Shuffle, Copy, Check } from 'lucide-react';

const MEDALS = ['🥇', '🥈', '🥉', '🏅', '🏅', '🏅', '🏅'];

const TeamsDivisionTab = ({ ctx }) => {
  const {
    players,
    teams,
    copied,
    handleCheckIn,
    handleUncheckAll,
    handleDrawTeams,
    handleCopyTeams,
  } = ctx;

  const [numTeams, setNumTeams] = useState(3);
  const presentCount = players.filter(p => p.is_present).length;
  const canDraw = presentCount >= numTeams;

  return (
    <div className="card">
      <h2 className="text-3xl font-bold mb-6">DIVISÃO DE TIMES</h2>

      {/* Check-in Section */}
      <div className="mb-8 p-6 border-2 border-blue-500 bg-blue-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-blue-700 flex items-center gap-2">
            ✓ CHECK-IN DOS JOGADORES
          </h3>
          <span className="text-lg font-bold text-blue-700">
            {presentCount} presentes
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
          {players.map((player) => (
            <label
              key={player.name}
              className={`flex items-center gap-2 p-3 border-2 cursor-pointer transition-colors ${
                player.is_present
                  ? 'bg-green-100 border-green-500'
                  : 'bg-white border-gray-300 hover:border-blue-400'
              }`}
              data-testid={`checkin-${player.name}`}
            >
              <input
                type="checkbox"
                checked={player.is_present || false}
                onChange={(e) => handleCheckIn(player.name, e.target.checked)}
                className="w-5 h-5 cursor-pointer"
                style={{ accentColor: '#22c55e' }}
              />
              <span className={`font-bold text-sm truncate ${player.is_present ? 'text-green-700' : 'text-gray-700'}`}>
                {player.is_titular && '⭐'}{player.name}
              </span>
            </label>
          ))}
        </div>

        {/* Number of teams selector */}
        <div className="mb-4 p-4 bg-white border-2 border-blue-300 rounded-lg">
          <p className="text-sm font-bold text-blue-700 mb-2 uppercase tracking-wide">Quantos times sortear?</p>
          <div className="flex flex-wrap gap-2">
            {[2, 3, 4, 5, 6, 7].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setNumTeams(n)}
                className={`px-4 py-2 rounded-lg font-black border-2 transition-colors ${
                  numTeams === n
                    ? 'bg-zinc-900 text-white border-zinc-900'
                    : 'bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500'
                }`}
                data-testid={`select-num-teams-${n}`}
              >
                {n} times
              </button>
            ))}
          </div>
          {presentCount > 0 && (
            <p className="text-xs text-zinc-500 mt-2">
              {canDraw
                ? `Cada time ficará com ~${Math.ceil(presentCount / numTeams)} jogador(es).`
                : `⚠️ Precisas de pelo menos ${numTeams} jogadores presentes para sortear ${numTeams} times.`}
            </p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleUncheckAll}
            className="btn-secondary flex-1"
            data-testid="uncheck-all-btn"
            disabled={presentCount === 0}
          >
            ✕ DESMARCAR TODOS ({presentCount})
          </button>
          <button
            onClick={() => handleDrawTeams(numTeams)}
            className="btn-primary flex-1"
            data-testid="draw-teams-btn"
            disabled={!canDraw}
          >
            <Shuffle className="inline mr-2" size={24} strokeWidth={3} />
            SORTEAR {numTeams} TIMES ({presentCount} jogadores)
          </button>
        </div>
      </div>

      {/* Teams Result */}
      {teams ? (
        <div>
          <h3 className="text-2xl font-bold mb-4">🎲 RESULTADO DO SORTEIO</h3>
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8`}>
            {teams.teams.map((team) => (
              <div key={team.number} className="border-2 border-black p-5" data-testid={`team-${team.number}`}>
                <h3 className="text-xl font-bold mb-3 uppercase tracking-wider text-center">
                  {MEDALS[team.number - 1] || '🏅'} TIME {team.number}
                </h3>
                <p className="text-base font-bold mb-3 text-center text-orange-600">
                  Capitão: {team.captain}
                </p>
                <div className="space-y-1.5">
                  {team.players.map((player, idx) => (
                    <p key={idx} className="text-base">
                      {idx === 0 ? '⭐' : '•'} {player}
                    </p>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-3 text-center font-bold">
                  {team.players.length} jogador{team.players.length === 1 ? '' : 'es'}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={handleCopyTeams}
            className="btn-primary w-full"
            data-testid="copy-teams-btn"
          >
            {copied ? (
              <><Check className="inline mr-2" size={24} strokeWidth={3} /> COPIADO!</>
            ) : (
              <><Copy className="inline mr-2" size={24} strokeWidth={3} /> COPIAR PARA WHATSAPP</>
            )}
          </button>
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300">
          <p className="text-xl font-bold text-gray-400 mb-2">Nenhum sorteio realizado</p>
          <p className="text-gray-500">Marca os jogadores presentes, escolhe quantos times e clica em &quot;Sortear&quot;</p>
        </div>
      )}
    </div>
  );
};

export default TeamsDivisionTab;
